/**
 * Core tracking functionality
 * 
 * MODIFY THIS FILE FOR:
 * - Core tracking logic (trackEvent, trackStandardEvent)
 * - User identification (identifyUser, setGA4UserId)
 * - Reusable helpers (delegateClick, getButtonText, extractCardData, etc.)
 * - Page type detection (getPageType)
 * 
 * DON'T MODIFY THIS FILE FOR:
 * - Feature-specific tracking → tracking.js
 * - Debug utilities → tracking-debug.js
 */
import TrackingDebug from './tracking-debug.js';

const TrackingCore = {
    // Store current user identification state
    _currentUserId: null, // Formatted user ID (peid_ prefix) - used for both Amplitude and GA4
    _currentAnonymousId: null,
    _currentIsRegistered: false,
    _currentIsAdmin: false,
    
    /**
     * Filter out null, undefined, and empty strings only
     * Keep 0 and false as valid values (e.g., results_count: 0)
     */
    filterEmptyValues(obj) {
        return Object.fromEntries(
            Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined && value !== '')
        );
    },

    /**
     * Generate UUID for anonymous user identification
     */
    generateUUID() {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback for older browsers
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    },

    /**
     * Get or create anonymous user ID
     */
    getAnonymousUserId() {
        const storageKey = 'amplitude_anonymous_id';
        let anonymousId = localStorage.getItem(storageKey);
        
        if (!anonymousId) {
            anonymousId = this.generateUUID();
            localStorage.setItem(storageKey, anonymousId);
        }
        
        return anonymousId;
    },

    /**
     * Format user ID with 'peid_' prefix for both Amplitude and GA4
     * Amplitude requires minimum 5 characters; prefix ensures compliance
     * @param {string|number} userId - Raw user ID from WordPress
     * @returns {string} Formatted user ID (e.g., 'peid_2', 'peid_123')
     */
    formatUserIdForAmplitude(userId) {
        if (!userId) return null;
        return `peid_${String(userId)}`;
    },

    /**
     * Resolve the cookie domain that both statesofmind.com and
     * community.statesofmind.com (Circle) can read.
     *
     * On the apex / community / app subdomains we want a single ".statesofmind.com"
     * cookie. Everywhere else (localhost, preview hosts, *.test, etc.)
     * we fall back to the bare hostname so the cookie still sets and we
     * don't pollute unrelated environments.
     *
     * @returns {string|null} Domain attribute for Set-Cookie, or null to omit it.
     */
    _resolveCommunityCookieDomain() {
        if (typeof window === 'undefined' || !window.location) return null;
        const host = (window.location.hostname || '').toLowerCase();
        if (!host) return null;
        if (host === 'statesofmind.com' || host.endsWith('.statesofmind.com')) {
            return '.statesofmind.com';
        }
        return null;
    },

    /**
     * Cross-domain device_id bridge: write the Amplitude device_id to a
     * cookie scoped to .statesofmind.com so the snippet inside Circle
     * (community.statesofmind.com) can pick it up on first load and call
     * amplitude.init({ deviceId }) with the same value. Without this, the
     * same person looks like two anonymous device_ids in Amplitude — one
     * per domain — and the funnel from article → community breaks.
     *
     * Called from identifyUser() so it re-runs on every page load.
     *
     * Safe to call before SDK loads: defers via waitForAmplitude(). Cookie
     * is also forwarded as am_device_id URL param by pe_circle_sso_render_handoff()
     * as a belt-and-braces fallback for environments where third-party
     * cookies or cross-subdomain cookies get stripped.
     */
    bridgeDeviceIdToCommunity() {
        if (typeof document === 'undefined') return;

        // Why a self-rescheduling poll instead of a single waitForAmplitude
        // callback?
        //
        // GTM exposes a proxy `window.amplitude` very early (so `track()` works
        // and queues events), but `getDeviceId()` on that proxy initially
        // returns null/undefined — the real device_id only becomes available
        // after the underlying analytics-browser SDK chunk has finished loading
        // and `_amplitude` is wired up.
        //
        // The previous implementation called `getDeviceId()` exactly once
        // inside the waitForAmplitude callback, hit the early-stage null,
        // and bailed permanently with `if (!did) return`. The bridge then
        // never wrote the `am_did` cookie, breaking cross-domain identity
        // for the rest of the page. Repro: load /community/, watch
        // document.cookie — `am_did` was never set automatically; manually
        // re-running `Tracking.init()` once the SDK had fully booted made
        // the cookie appear instantly.
        //
        // We now keep polling until either getDeviceId() returns a real id
        // or we hit a generous 20-second budget. 20s comfortably covers
        // slow connections / cold GTM containers; in practice the cookie
        // typically lands within ~5–10s of navigationStart.
        const maxAttempts = 200; // 200 * 100ms = 20s
        let attempts = 0;

        const tryWriteCookie = () => {
            try {
                const did = (window.amplitude && typeof window.amplitude.getDeviceId === 'function')
                    ? window.amplitude.getDeviceId()
                    : null;
                if (!did) {
                    if (attempts++ < maxAttempts) {
                        setTimeout(tryWriteCookie, 100);
                    } else if (TrackingDebug.isEnabled()) {
                        console.warn('[Tracking Debug] bridgeDeviceIdToCommunity gave up: getDeviceId() never returned a value');
                    }
                    return;
                }

                const domain = this._resolveCommunityCookieDomain();
                const secure = window.location.protocol === 'https:';

                // 2 years — same horizon Amplitude uses for its own
                // device_id storage; keeps cookie alive across long pauses.
                const maxAge = 60 * 60 * 24 * 365 * 2;
                let cookie = `am_did=${encodeURIComponent(did)}; path=/; max-age=${maxAge}; SameSite=Lax`;
                if (domain) cookie += `; domain=${domain}`;
                if (secure) cookie += '; Secure';

                document.cookie = cookie;

                if (TrackingDebug.isEnabled()) {
                    console.log('[Tracking Debug] am_did cookie set for cross-domain Amplitude:', did, domain || '(host-only)');
                }
            } catch (error) {
                console.warn('[Tracking] bridgeDeviceIdToCommunity error:', error);
            }
        };

        // Kick off via waitForAmplitude so we don't even start polling
        // until `window.amplitude` exists at all (avoids burning attempts
        // before GTM has loaded). Once it's there, tryWriteCookie handles
        // the secondary "but is getDeviceId() *real* yet?" wait itself.
        this.waitForAmplitude(tryWriteCookie, 50);
    },

    /**
     * Wait for Amplitude to load via GTM
     */
    waitForAmplitude(callback, maxAttempts = 50) {
        let attempts = 0;
        const checkAmplitude = () => {
            attempts++;
            if (typeof window.amplitude !== 'undefined' && window.amplitude.track) {
                if (TrackingDebug.isEnabled()) {
                    console.log('[Tracking Debug] Amplitude loaded via GTM');
                }
                callback();
            } else if (attempts < maxAttempts) {
                setTimeout(checkAmplitude, 100);
            } else {
                if (TrackingDebug.isEnabled()) {
                    console.warn('[Tracking Debug] Amplitude not loaded via GTM after maximum attempts');
                }
            }
        };
        checkAmplitude();
    },

    /**
     * Wait for dataLayer to load via GTM
     */
    waitForDataLayer(callback, maxAttempts = 50) {
        let attempts = 0;
        const checkDataLayer = () => {
            attempts++;
            if (typeof dataLayer !== 'undefined') {
                if (TrackingDebug.isEnabled()) {
                    console.log('[Tracking Debug] dataLayer loaded via GTM');
                }
                callback();
            } else if (attempts < maxAttempts) {
                setTimeout(checkDataLayer, 100);
            } else {
                if (TrackingDebug.isEnabled()) {
                    console.warn('[Tracking Debug] dataLayer not loaded via GTM after maximum attempts');
                }
            }
        };
        checkDataLayer();
    },

    /**
     * Track event with Amplitude
     */
    trackWithAmplitude(eventName, eventProperties) {
        const amplitudeAvailable = typeof window.amplitude !== 'undefined' && window.amplitude.track;
        
        if (amplitudeAvailable) {
            TrackingDebug.log('Amplitude', eventName, eventProperties, 'sent');
            window.amplitude.track(eventName, eventProperties);
        } else {
            TrackingDebug.log('Amplitude', eventName, eventProperties, 'pending');
            this.waitForAmplitude(() => {
                TrackingDebug.log('Amplitude', eventName, eventProperties, 'sent');
                window.amplitude.track(eventName, eventProperties);
            }, 30);
        }
    },

    /**
     * Send event to GA4 via dataLayer
     */
    sendGA4Event(eventName, eventProperties) {
        const ga4EventData = {
            'event': eventName,
            'event_category': 'Website Interaction',
            'event_label': eventProperties.page_type || 'general',
            ...eventProperties
        };
        
        // Include user identification in events
        if (this._currentUserId) {
            ga4EventData.user_id = this._currentUserId;
        } else if (this._currentAnonymousId) {
            ga4EventData.anonymous_id = this._currentAnonymousId;
        }
        
        TrackingDebug.log('GA4', eventName, ga4EventData, 'sent');
        
        if (typeof dataLayer !== 'undefined') {
            dataLayer.push(ga4EventData);
        } else {
            console.warn('[Tracking] dataLayer not available, event not sent:', eventName);
        }
    },

    /**
     * Track event with GA4
     */
    trackWithGA4(eventName, eventProperties) {
        const dataLayerAvailable = typeof dataLayer !== 'undefined';
        
        if (dataLayerAvailable) {
            this.sendGA4Event(eventName, eventProperties);
        } else {
            const ga4EventData = {
                'event': eventName,
                'event_category': 'Website Interaction',
                'event_label': eventProperties.page_type || 'general',
                ...eventProperties
            };
            
            if (this._currentUserId) {
                ga4EventData.user_id = this._currentUserId;
            } else if (this._currentAnonymousId) {
                ga4EventData.anonymous_id = this._currentAnonymousId;
            }
            
            TrackingDebug.log('GA4', eventName, ga4EventData, 'pending');
            this.waitForDataLayer(() => this.sendGA4Event(eventName, eventProperties), 30);
        }
    },

    /**
     * Get locale from document lang attribute (fallback when pe_mp_tracking_config is not set)
     * Returns locale as-is (e.g. en_GB or en-GB). Defaults to "en_GB" when missing.
     * @returns {string} Locale code as from document or default
     */
    getLocaleFromDocument() {
        const lang = typeof document !== 'undefined' && document.documentElement?.lang;
        if (!lang || typeof lang !== 'string') return 'en_GB';
        return lang;
    },

    /**
     * Get browser user agent for analytics (enables bot/crawler filtering in Amplitude).
     * @returns {string} navigator.userAgent or empty string when unavailable
     */
    getUserAgent() {
        return (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
    },

    /**
     * Detect Ahrefs Site Audit / crawler so we can exclude from Amplitude and avoid inflating metrics.
     * @returns {boolean}
     */
    isAhrefsCrawler() {
        return /ahrefs/i.test(this.getUserAgent());
    },

    /**
     * Track event with both Amplitude and GA4
     * @param {string} eventName - Event name from TRACKING_EVENTS constants
     * @param {object} eventProperties - Event properties/parameters
     */
    trackEvent(eventName, eventProperties = {}) {
        const filteredProperties = this.filterEmptyValues(eventProperties);

        filteredProperties.domain = window.pe_mp_tracking_config?.domain ?? (typeof window !== 'undefined' ? window.location.hostname : '');
        filteredProperties.locale = window.pe_mp_tracking_config?.locale ?? this.getLocaleFromDocument();

        const ua = this.getUserAgent();
        if (ua) filteredProperties.user_agent = ua;
        
        // Include user identification properties in event properties (for filtering)
        if (this._currentUserId) {
            filteredProperties.user_id = this._currentUserId;
        }
        
        filteredProperties.is_registered = Boolean(this._currentIsRegistered);
        filteredProperties.is_admin = Boolean(this._currentIsAdmin);
        
        if (!this._currentUserId && this._currentAnonymousId) {
            filteredProperties.anonymous_id = this._currentAnonymousId;
        }
        
        if (!this.isAhrefsCrawler()) {
            this.trackWithAmplitude(eventName, filteredProperties);
        }
        this.trackWithGA4(eventName, filteredProperties);
        
        // Simple log if debug is disabled
        if (!TrackingDebug.isEnabled()) {
            console.log(`[Tracking] ${eventName}`, filteredProperties);
        }
    },

    /**
     * Set GA4 user identification in dataLayer
     * GA4 uses user_id from dataLayer for user identification
     * @param {string|null} userId - User ID for registered users, null for anonymous
     * @param {string} anonymousId - Anonymous ID for anonymous users
     */
    setGA4UserId(userId, anonymousId) {
        this._currentUserId = userId ? this.formatUserIdForAmplitude(userId) : null;
        this._currentAnonymousId = anonymousId;
        
        this.waitForDataLayer(() => {
            const ga4UserData = {};
            
            if (userId) {
                ga4UserData.user_id = this._currentUserId;
            } else {
                ga4UserData.user_id = null;
                ga4UserData.anonymous_id = anonymousId;
            }
            
            if (typeof dataLayer !== 'undefined') {
                dataLayer.push(ga4UserData);
            }
            
            if (TrackingDebug.isEnabled()) {
                TrackingDebug.log('GA4', 'User Identified', ga4UserData, 'sent');
            } else {
                console.log('[Tracking] GA4 user identified:', ga4UserData);
            }
        }, 30);
    },

    /**
     * Identify user in both Amplitude and GA4
     * @param {object} userData - User data object with user_id, is_registered, is_admin
     */
    identifyUser(userData = {}) {
        const { user_id, is_registered, is_admin } = userData;
        const isRegistered = Boolean(is_registered);
        const isAdmin = Boolean(is_admin === true || is_admin === '1' || is_admin === 1);
        const anonymousId = this.getAnonymousUserId();
        
        this._currentIsRegistered = isRegistered;
        this._currentIsAdmin = isAdmin;
        
        this.setGA4UserId(isRegistered && user_id ? user_id : null, anonymousId);
        
        this.waitForAmplitude(() => {
            const formattedUserId = isRegistered && user_id ? this.formatUserIdForAmplitude(user_id) : null;
            
            const props = isRegistered && user_id
                ? { user_id: formattedUserId, is_registered: true, is_admin: isAdmin }
                : { is_registered: false, is_admin: false, anonymous_id: anonymousId };
            
            // Set user ID first (separate from user properties)
            if (window.amplitude.setUserId) {
                window.amplitude.setUserId(formattedUserId);
            }
            
            // Remove user_id from props as it's set via setUserId
            const userProps = { ...props };
            delete userProps.user_id;
            
            // Try modern Identify API first (new amplitude.Identify())
            let identifySuccess = false;
            if (typeof window.amplitude.Identify === 'function') {
                try {
                    const identify = new window.amplitude.Identify();
                    if (identify && typeof identify.set === 'function') {
                        Object.entries(userProps).forEach(([key, value]) => {
                            identify.set(key, value);
                        });
                        window.amplitude.identify(identify);
                        identifySuccess = true;
                    }
                } catch (error) {
                    console.warn('[Tracking] Amplitude Identify API error:', error);
                }
            }
            
            // Fallback: Use setUserProperties (legacy API - accepts plain object)
            if (!identifySuccess && typeof window.amplitude.setUserProperties === 'function') {
                try {
                    window.amplitude.setUserProperties(userProps);
                    identifySuccess = true;
                } catch (error) {
                    console.warn('[Tracking] Amplitude setUserProperties error:', error);
                }
            }
            
            if (TrackingDebug.isEnabled()) {
                TrackingDebug.log('Amplitude', 'User Identified', props, 'sent');
            } else {
                console.log('[Tracking] User identified:', props);
            }
        }, 50);

        // Keep .statesofmind.com cookie am_did in sync with the current
        // Amplitude device_id so the Circle community can stitch the
        // visitor into the same Amplitude user on first load.
        this.bridgeDeviceIdToCommunity();
    },

    /**
     * Helper Methods - Reusable utilities
     */

    /**
     * Get page type from URL
     */
    getPageType() {
        if (typeof document !== 'undefined' && document.body) {
            if (document.body.classList.contains('page-template-test-report') ||
                document.body.classList.contains('page-template-test-report-v2')) {
                return 'test_report';
            }
        }

        const pathname = window.location.pathname.toLowerCase();
        const pathParts = pathname.split('/').filter(part => part.length > 0);

        if (pathname === '/' || pathname === '') return 'home';
        
        const pageTypeMap = {
            '/category/': 'category_page',
            '/substance-guides': 'category_page',
            '/tag/': 'tag_page',
            '/community/': 'community_page',
            '/my-account/': 'account_page',
            '/providers/type/': 'providers_list',
            '/providers/list/': 'providers_list',
            '/facility-type/': 'providers_list'
        };

        for (const [prefix, type] of Object.entries(pageTypeMap)) {
            if (pathname.startsWith(prefix)) return type;
        }

        // Provider pages with special handling
        if (pathname.startsWith('/providers/')) {
            if (pathname.startsWith('/providers/search/') || pathname === '/providers/' || pathParts.length === 1) {
                return 'providers_list';
            }
            if (pathParts.length > 1 && pathParts[1] === 'provider-tag') {
                return 'providers_list';
            }
            const specialPaths = ['get-verified'];
            if (pathParts.length > 1 && specialPaths.includes(pathParts[1])) {
                return 'general';
            }
            return pathParts.length > 1 && pathParts[1] !== 'search' ? 'provider_page' : 'providers_list';
        }
        
        // Screening / quiz pages (including nested /toolkit/screening/)
        const screeningIdx = pathParts.indexOf('screening');
        const testsIdx = pathParts.indexOf('tests');
        const testIdx = pathParts.indexOf('test');
        const hubIdx = screeningIdx !== -1 ? screeningIdx : (testsIdx !== -1 ? testsIdx : testIdx);
        if (hubIdx !== -1) {
            return pathParts.length > hubIdx + 1 ? 'screening_page' : 'screenings_list';
        }
        
        // Psychedelic Evidence Tracker: hub and its substance/condition pages
        if (pathname.startsWith('/psychedelic-evidence-tracker/')) {
            return pathParts.length > 1 ? 'evidence_tracker_page' : 'evidence_tracker';
        }

        // Article pages
        if (pathname.startsWith('/articles/')) {
            return pathParts.length > 1 && pathParts[1] ? 'article_page' : 'articles_list';
        }
        
        // Expert/Author pages
        const expertPrefixes = ['/authors/', '/expert-review-board/'];
        for (const prefix of expertPrefixes) {
            if (pathname.startsWith(prefix)) {
                return pathParts.length > 1 && pathParts[1] ? 'expert_page' : 'experts_list';
            }
        }
        
        return 'general';
    },

    /**
     * Track event with standard properties (page_type, page_url)
     * @param {string} eventName - Event name from TRACKING_EVENTS
     * @param {object} eventProperties - Additional properties
     */
    trackStandardEvent(eventName, eventProperties = {}) {
        this.trackEvent(eventName, {
            page_type: this.getPageType(),
            page_url: window.location.href,
            ...eventProperties
        });
    },

    /**
     * Delegate click events to elements
     * @param {string} selector - CSS selector
     * @param {function} callback - Callback(element, event)
     * @param {Element} root - Root element (default: document.body)
     */
    delegateClick(selector, callback, root = document.body, useCapture = false) {
        root.addEventListener('click', (e) => {
            const element = e.target.closest(selector);
            if (element) callback(element, e);
        }, useCapture);
    },

    /**
     * Extract button text from element
     * @param {Element} element - Button/link element
     * @param {string|null} fallback - Fallback text
     * @returns {string} Button text
     */
    getButtonText(element, fallback = null) {
        return element.textContent?.trim() || element.dataset.buttonText || element.dataset.ctaButtonText || fallback;
    },

    /**
     * Extract card data (title and URL)
     * @param {Element} card - Card element
     * @param {string|Array} titleSelectors - Title selectors (default: ['h3', 'h4', '.card__title'])
     * @returns {object} {title, url}
     */
    extractCardData(card, titleSelectors = ['h3', 'h4', '.card__title']) {
        const selectors = Array.isArray(titleSelectors) ? titleSelectors.join(', ') : titleSelectors;
        return {
            title: card.querySelector(selectors)?.textContent?.trim() || 'Unknown',
            url: card.href || card.querySelector('a')?.href || null
        };
    },

    /**
     * Track clicks on card elements
     * @param {NodeList|Array} cards - Card elements
     * @param {function} trackFn - Tracking function(eventName, properties)
     * @param {string} eventName - Event name
     * @param {function} propsFn - Properties extractor(card, index)
     */
    trackCardClicks(cards, trackFn, eventName, propsFn) {
        cards.forEach((card, index) => {
            card.addEventListener('click', (e) => {
                if (e.target.closest('button') || e.target.closest('.btn')) return;
                trackFn(eventName, propsFn(card, index));
            });
        });
    }
};

export default TrackingCore;
